import React, {useState, useEffect} from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { forceRefreshUser, hasActiveSubscription, registerUserRefreshCallback, triggerGlobalUserRefresh } from "./utils/userUtils";
import Home from "./pages/Home";
import Login from "./pages/Login";
import Register from "./pages/Register";
import VerifyEmail from "./pages/VerifyEmail";
import DashboardSelection from "./pages/DashboardSelection";
import TrialBanner from "./components/TrialBanner";
import EnhancedMathChatbot from "./components/EnhancedMathChatbot";

// MathVenture App - Firebase Removed - Clean Build v2
// Build timestamp: 2025-01-20
import SubscriptionPage from "./pages/SubscriptionPage";
import Quiz from "./pages/Quiz";
import Leaderboard from "./pages/Leaderboard";
import Dashboard from "./pages/Dashboard";
import Games from "./pages/Games";
import AdminDashboard from "./components/AdminDashboard";
// Class 4 & 5 Games
import MultiplicationMonsters from "./games/MultiplicationMonsters";
import DivisionDash from "./games/DivisionDash";
import FractionPizza from "./games/FractionPizza";
import TimeTravel from "./games/TimeTravel";
import CoinQuest from "./games/CoinQuest";
import GeometryBuilder from "./games/GeometryBuilder";
import DecimalDragons from "./games/DecimalDragons";
import PatternMaster from "./games/PatternMaster";
import NumberShooting from "./games/NumberShooting";
import WordProblemAdventure from "./games/WordProblemAdventure";
// Class 5 Specific Games
import AngleAdventure from "./games/AngleAdventure";
import PercentagePower from "./games/PercentagePower";
import AlgebraQuest from "./games/AlgebraQuest";
import VolumeVault from "./games/VolumeVault";
// New Exciting Class 5 Games
import TreasureHuntMath from "./games/TreasureHuntMath";
import MathShooterPro from "./games/MathShooterPro";
import SpeedRacerMath from "./games/SpeedRacerMath";
import MathNinjaWarrior from "./games/MathNinjaWarrior";
import RocketMathRace from "./games/RocketMathRace";
// General Games (Available for All Users)
import WordPuzzleMaster from "./games/WordPuzzleMaster";
import MemoryMatchMath from "./games/MemoryMatchMath";
import PuzzleUnlock from "./games/PuzzleUnlock";
import DragDropMatch from "./games/DragDropMatch";
import NumberRunner from "./games/NumberRunner";
import Header from "./components/Header";
import UserProfile from "./components/UserProfile";
import Pricing from "./pages/Pricing";
import Payment from "./pages/Payment";

export default function App() {
  const [user, setUser] = useState({id: -1, username: "Guest"});
  const [showProfile, setShowProfile] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Load user from localStorage on app start
  useEffect(() => {
    const loadUser = async () => {
      try {
        const savedUser = localStorage.getItem('user');
        console.log('Loading saved user:', savedUser); // Debug log
        
        if (savedUser && savedUser !== 'undefined' && savedUser !== 'null') {
          const userData = JSON.parse(savedUser);
          console.log('Parsed user data:', userData); // Debug log
          
          // Validate user data before setting - check for required fields
          if (userData && 
              typeof userData === 'object' && 
              userData.id && 
              userData.id !== -1 && 
              userData.username) {
            // More lenient validation - only require id and username
            console.log('User validation passed:', {
              hasId: !!userData.id,
              idValue: userData.id,
              hasUsername: !!userData.username,
              hasEmail: !!userData.email,
              userType: typeof userData
            });
            setUser(userData);
            console.log('User loaded successfully:', userData.username); // Debug log
          } else {
            console.log('Invalid user data structure:', {
              userData: userData,
              hasId: userData?.id,
              idValue: userData?.id,
              hasUsername: userData?.username,
              hasEmail: userData?.email,
              userType: typeof userData
            });
            console.log('Removing invalid user data from localStorage');
            localStorage.removeItem('user');
            setUser({id: -1, username: "Guest"});
          }
        } else {
          console.log('No valid saved user found');
          setUser({id: -1, username: "Guest"});
        }
      } catch (error) {
        console.error('Error loading user data:', error);
        localStorage.removeItem('user');
        setUser({id: -1, username: "Guest"});
      } finally {
        setIsLoading(false);
      }
    };

    loadUser();
    
    // Register global user refresh callback
    const unregister = registerUserRefreshCallback((updatedUser) => {
      console.log('🔄 Global user refresh triggered in App.jsx:', updatedUser);
      setUser(updatedUser);
    });
    
    return unregister;
  }, []);

  const handleLogout = () => {
    console.log('Logging out user'); // Debug log
    setUser({id: -1, username: "Guest"});
    localStorage.removeItem('user');
    // Clear any other user-related data
    localStorage.removeItem('enhanced_mathbot_history');
    setShowProfile(false);
  };

  const handleSubscriptionUpdate = (subscriptionData) => {
    // Update user data with new subscription info
    setUser(prevUser => {
      const updatedUser = {
        ...prevUser,
        subscription: subscriptionData
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      return updatedUser;
    });
  };

  const handleUpdateUser = (updatedUser) => {
    // Update user data from profile changes
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  // Function to refresh user data from backend
  const refreshUserFromBackend = async () => {
    if (user && user.id && user.id !== -1) {
      console.log('Refreshing user data from backend...');
      const freshUser = await forceRefreshUser(user.id, setUser);
      if (freshUser) {
        console.log('User data refreshed successfully:', freshUser);
        return freshUser;
      }
    }
    return null;
  };

  // Expose refresh function globally for payment success and admin actions
  useEffect(() => {
    window.refreshUserData = refreshUserFromBackend;
    window.triggerGlobalUserRefresh = triggerGlobalUserRefresh;
    return () => {
      delete window.refreshUserData;
      delete window.triggerGlobalUserRefresh;
    };
  }, [user]);

  // Auto-refresh user data every 5 minutes to catch subscription changes
  useEffect(() => {
    if (user && user.id && user.id !== -1) {
      const interval = setInterval(() => {
        refreshUserFromBackend();
      }, 5 * 60 * 1000); // 5 minutes
      
      return () => clearInterval(interval);
    }
  }, [user]);

  // Save user data whenever user state changes (except for guest)
  useEffect(() => {
    if (user && 
        typeof user === 'object' && 
        user.id && 
        user.id !== -1 && 
        user.username && 
        user.email) {
      console.log('Saving user data to localStorage:', user.username); // Debug log
      localStorage.setItem('user', JSON.stringify(user));
    } else if (user && user.id === -1) {
      // User is guest, don't save but also don't remove existing data unless explicitly logged out
      console.log('User is guest, not saving to localStorage');
    }
  }, [user]);

  // Protected Route Component
  const ProtectedRoute = ({ children, requireAuth = true, requireAdmin = false }) => {
    // Show loading while checking user state
    if (isLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    if (requireAuth && user.id === -1) {
      return <Navigate to="/login" replace />;
    }
    if (requireAdmin && !user.is_admin) {
      return <Navigate to="/dashboard" replace />;
    }
    return children;
  };

  // Component to handle routing with location-aware header
  const AppContent = () => {
    const location = useLocation();
    const hideHeaderPaths = ['/login', '/register', '/verify-email', '/dashboard-selection', '/admin-dashboard'];
    const showHeader = !hideHeaderPaths.includes(location.pathname);
    const showContainer = !hideHeaderPaths.includes(location.pathname);

    return (
      <div className="min-h-screen">
        {/* Show header on most pages */}
        {showHeader && (
          <>
            <TrialBanner user={user} />
            <Header user={user} onLogout={handleLogout} onUpdateUser={handleUpdateUser} onShowProfile={() => setShowProfile(true)} />
          </>
        )}
        <main className={showContainer ? "container mx-auto p-2 sm:p-4" : ""}>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login setUser={setUser} />} />
            <Route path="/register" element={<Register setUser={setUser} />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/guest" element={<Home setUser={setUser} />} />
            
            {/* Protected Routes */}
            <Route path="/" element={
              isLoading ? (
                <div className="min-h-screen flex items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              ) : user.id === -1 ? <Navigate to="/login" replace /> : <Navigate to="/dashboard" replace />
            } />
            
            <Route path="/dashboard-selection" element={
              <ProtectedRoute requireAuth={true} requireAdmin={true}>
                <DashboardSelection user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            } />
            
            <Route path="/admin-dashboard" element={
              <ProtectedRoute requireAuth={true} requireAdmin={true}>
                <AdminDashboard user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            } />
            
            <Route path="/subscription-manager" element={
              <ProtectedRoute requireAuth={true}>
                <SubscriptionPage user={user} onSubscriptionUpdate={handleSubscriptionUpdate} />
              </ProtectedRoute>
            } />
            
            <Route path="/dashboard" element={<Dashboard user={user} setUser={setUser} />} />
            
            <Route path="/quiz/:classId" element={<Quiz user={user} />} />
            
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/games" element={<Games />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/payment" element={<Payment />} />
            
            {/* Class 4 Games */}
            <Route path="/games/multiplication-monsters" element={<MultiplicationMonsters />} />
            <Route path="/games/division-dash" element={<DivisionDash />} />
            <Route path="/games/fraction-pizza" element={<FractionPizza />} />
            <Route path="/games/time-travel" element={<TimeTravel />} />
            <Route path="/games/coin-quest" element={<CoinQuest />} />
            <Route path="/games/geometry-builder" element={<GeometryBuilder />} />
            <Route path="/games/decimal-dragons" element={<DecimalDragons />} />
            <Route path="/games/pattern-master" element={<PatternMaster />} />
            <Route path="/games/number-shooting" element={<NumberShooting />} />
            <Route path="/games/word-problem-adventure" element={<WordProblemAdventure />} />
            
            {/* Class 5 Games */}
            <Route path="/games/angle-adventure" element={<AngleAdventure />} />
            <Route path="/games/percentage-power" element={<PercentagePower />} />
            <Route path="/games/algebra-quest" element={<AlgebraQuest />} />
            <Route path="/games/volume-vault" element={<VolumeVault />} />
            <Route path="/games/treasure-hunt-math" element={<TreasureHuntMath />} />
            <Route path="/games/math-shooter-pro" element={<MathShooterPro />} />
            <Route path="/games/speed-racer-math" element={<SpeedRacerMath />} />
            <Route path="/games/math-ninja-warrior" element={<MathNinjaWarrior />} />
            <Route path="/games/rocket-math-race" element={<RocketMathRace />} />
            
            {/* General Games - Available for All Users */}
            <Route path="/games/word-puzzle-master" element={<WordPuzzleMaster />} />
            <Route path="/games/memory-match-math" element={<MemoryMatchMath />} />
            <Route path="/games/puzzle-unlock" element={<PuzzleUnlock />} />
            <Route path="/games/drag-drop-match" element={<DragDropMatch />} />
            <Route path="/games/number-runner" element={<NumberRunner />} />
          </Routes>
        </main>
        <EnhancedMathChatbot />
        
        {/* User Profile Modal - Rendered at app level to appear over everything */}
        {showProfile && user && user.id !== -1 && (
          <UserProfile
            user={user}
            onClose={() => setShowProfile(false)}
            onUpdateUser={handleUpdateUser}
          />
        )}
      </div>
    );
  };

  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}
